import { useNavigate, useLocation, Location } from "react-router-dom";
import { authService, setAccessToken } from "@b2b/services";
import { getFirstGrantedAdminPath } from "@b2b/ui-components";

interface LocationState {
  from?: Pick<Location, "pathname" | "search" | "hash">;
}

export function buildLoginReturnState(
  pathname: string,
  search = "",
  hash = "",
): { from: Pick<Location, "pathname" | "search" | "hash"> } {
  return { from: { pathname, search, hash } };
}

export function buildPathFromStoredLocation(from: Pick<Location, "pathname" | "search" | "hash">): string {
  const base = from.pathname || "/";
  const search =
    from.search && String(from.search).length > 0
      ? String(from.search).startsWith("?")
        ? from.search
        : `?${from.search}`
      : "";
  const hash = from.hash || "";
  return `${base}${search}${hash}`;
}

/**
 * Hook to handle post-login redirect logic
 * Works for both Auth0 and email/password login flows
 */
export const usePostLoginRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Handles post-login flow:
   * 1. Stores access token
   * 2. Calls GET /api/v1/me to check user status
   * 3. Routes based on status and role
   */
  const handlePostLogin = async (accessToken: string): Promise<void> => {
    try {
      console.log("🔑 Step 1: Token received:", accessToken?.substring(0, 20) + "...");
      
      // Store the token in localStorage for axios client to use
      setAccessToken(accessToken);
      console.log("✅ Step 2: Token stored in localStorage");

      // Give a small delay to ensure token is stored and AuthProvider detects it
      await new Promise(resolve => setTimeout(resolve, 100));

      // Check user status with GET /me
      console.log("📞 Step 3: Calling /api/v1/me...");
      const meResponse = await authService.getMe();
      console.log("📥 Step 4: /api/v1/me response:", meResponse);
      console.log("📊 Step 4a: Status:", meResponse.status);
      console.log("📊 Step 4b: User role:", meResponse.user?.role);

      const state = location.state as LocationState | null;
      console.log("📍 Step 5: Location state:", state);

      // Handle redirect based on status
      if (state?.from?.pathname) {
        const target = buildPathFromStoredLocation(state.from);
        console.log("🔄 Step 6: Redirecting to original page:", target);
        navigate(target, { replace: true });
        return;
      }

      if (meResponse.status === "not_registered") {
        // User not registered - redirect to signup (OAuth will land on role selection with email)
        console.log("❌ Step 7: User not registered, redirecting to signup");
        navigate("/login?tab=signup", {
          replace: true,
          state: {
            fromOAuth: true,
            prefilledEmail: meResponse.user?.email ?? undefined,
          },
        });
        return;
      }

      if (meResponse.status === "pending_registration") {
        // OAuth user must complete role selection + form - go to signup tab with email prefilled
        console.log("⏳ Step 8: User pending registration, redirecting to signup");
        navigate("/login?tab=signup", {
          replace: true,
          state: {
            fromOAuth: true,
            prefilledEmail: meResponse.user?.email ?? undefined,
          },
        });
        return;
      }

      if (meResponse.status === "active") {
        // User is fully registered and active - Navigate to profile_settings (buyer/seller) or admin dashboard
        const role = meResponse.user?.role?.toLowerCase();
        console.log("✅ Step 9: User is active, role:", role);

        let targetPath = "/buyer/profile_settings"; // Default: land on profile
        if (role === "super_admin" || role === "company_admin") {
          targetPath = "/admin/dashboard";
        } else if (role === "staff") {
          targetPath = getFirstGrantedAdminPath(meResponse.user?.allowedModules || []);
        } else if (role === "seller" || role === "buyer_seller") {
          targetPath = "/seller/profile_settings";
        } else if (role === "buyer") {
          targetPath = "/buyer/profile_settings";
        }
        console.log("🚀 Step 10: Navigating to", targetPath);
        
        // Use window.location for more reliable navigation
        if (window.location.pathname !== targetPath) {
          navigate(targetPath, { replace: true });
          // Force navigation if React Router doesn't work
          setTimeout(() => {
            if (window.location.pathname !== targetPath) {
              window.location.href = targetPath;
            }
          }, 100);
        }
        return;
      }

      // Fallback: redirect to signup if status is unknown
      console.log("⚠️ Step 11: Unknown status, redirecting to signup");
      navigate("/login?tab=signup", { replace: true });
    } catch (error: any) {
      console.error("❌ Error in post-login redirect:", error);
      console.error("❌ Error details:", error.response?.data || error.message);
      console.error("❌ Error stack:", error.stack);
      // On error, redirect to signup
      navigate("/login?tab=signup", { replace: true });
    }
  };

  return { handlePostLogin };
};

